import React, { useEffect, useState } from 'react'
import { BiSolidUser } from 'react-icons/bi'
import { FaWallet } from 'react-icons/fa'
import { AiFillSave } from 'react-icons/ai'
import { PiBankDuotone } from 'react-icons/pi'
import { Select, Switch, Input, InputNumber, Spin } from 'antd'
import image from '../../../assests/icons/agentimage.png'
import { changeHeader } from '../../../redux/reducer/headerSlice'
import { useDispatch } from 'react-redux'
import handleGet from '../../../utils/Get'
import { BASE_URL } from '../../../utils/config'
import handlePost from '../../../utils/Post'
import { useTranslation } from 'react-i18next'

/* ─── Nav items ─────────────────────────── */
const NAV_ITEMS = [
  { key: true,  icon: BiSolidUser, labelKey: 'General' },
  { key: false, icon: FaWallet,    labelKey: 'ManageAgents' },
]

/* ─── Shared atoms ──────────────────────── */
const Field = ({ label, children }) => (
  <div className="mb-4">
    <label className="block text-xs font-semibold text-[#8B8FA8] uppercase tracking-widest mb-1.5">
      {label}
    </label>
    {children}
  </div>
)

const GlowBtn = ({ children, onClick, loading, variant = 'primary', disabled = false, className = '' }) => {
  const base = 'inline-flex items-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed'
  const variants = {
    primary: 'bg-[#6C5DD3] hover:bg-[#7B6AE0] text-white shadow-[0_0_20px_rgba(108,93,211,0.3)] hover:shadow-[0_0_30px_rgba(108,93,211,0.5)]',
    ghost:   'bg-[#161826] border border-[#2A2D3E] hover:border-[#6C5DD3]/50 text-[#8B8FA8] hover:text-white',
    success: 'bg-[#0D1A12] border border-emerald-500/40 hover:border-emerald-500/70 text-emerald-400 hover:text-emerald-300',
  }
  return (
    <button onClick={onClick} disabled={loading || disabled} className={`${base} ${variants[variant]} ${className}`}>
      {loading ? <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : children}
    </button>
  )
}

const darkInputStyle = { background: '#0D0F1A', borderColor: '#2A2D3E', color: '#fff', borderRadius: '8px' }

/* ══════════════════════════════════════════════════════ */
const AgencyManagement = () => {
  const [tabManage, setTabManage]     = useState(true)
  const [buttonManage, setButtonManage] = useState(true)
  const [loading, setLoading]         = useState({ main: true, general: false, agent: false })
  const [users, setUsers]             = useState('')
  const [accountData, setAccountData] = useState({ acount_holder_name: '', bank: 'swift', bank_name: '', account_number: '', require_kyc: 1 })
  const [selectUser, setSelectUser]   = useState('')
  const [agents, setAgents]           = useState('')
  const dispatch                      = useDispatch()

  const { t } = useTranslation()
  const {
    UsersManagement, General, ManageAgents, Settings, Edit, Bank, Agency,
    Save, BSB, Swift, AccountHolder, BankName, BankAccount, Require, Wheather,
    Enabled, RequireKyc, ManageUsers, NoAgents, AddAgents, AgencyBank,
  } = t('AgencyManagement', { returnObjects: true })

  useEffect(() => {
    dispatch(changeHeader(UsersManagement))
    fetchUsers('')
    fetchAgentData()
    handleGetAgent()
  }, [UsersManagement])

  const fetchUsers = async (value) => {
    const res = await handleGet(`${BASE_URL}get/all/users?search=${value}`)
    setUsers(res?.users)
  }

  const fetchAgentData = async () => {
    try {
      const res = await handleGet(`${BASE_URL}agency/get/general-data`)
      setAccountData(res?.agency_data)
      setLoading(p => ({ ...p, main: false }))
    } catch (e) { console.log(e) }
  }

  const handleGetAgent = async () => {
    try {
      const res = await handleGet(`${BASE_URL}agency/agents/all`)
      setAgents(res?.agents)
    } catch (e) { console.log(e) }
  }

  const onChange = (value) => setSelectUser(value)
  const onSearch = (value) => fetchUsers(value)

  const handleAddAgent = async () => {
    setLoading(p => ({ ...p, agent: true }))
    try {
      await handlePost(`${BASE_URL}agency/add/agent`, { user_id: selectUser })
      setSelectUser('')
      handleGetAgent()
    } catch (e) { console.log(e) }
    finally { setLoading(p => ({ ...p, agent: false })) }
  }

  const handleUpdateAgent = async () => {
    setLoading(p => ({ ...p, general: true }))
    try {
      await handlePost(`${BASE_URL}agency/update/general-data`, accountData)
    } catch (e) { console.log(e) }
    finally { setLoading(p => ({ ...p, general: false })) }
  }

  /* ── agent initials helper ── */
  const initials = (name = '') => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?'
  const avatarColors = ['bg-violet-500', 'bg-blue-500', 'bg-emerald-500', 'bg-rose-500', 'bg-amber-500', 'bg-cyan-500']
  const agentColor = (id) => avatarColors[id % avatarColors.length]

  if (loading?.main) return (
    <div className="min-h-[60vh] bg-[#080A12] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-2 border-[#6C5DD3] border-t-transparent rounded-full animate-spin" />
        <p className="text-[#8B8FA8] text-sm">Loading agency data...</p>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-[#080A12] p-3 sm:p-4 lg:p-6 font-sans">
      <div className="max-w-6xl mx-auto">

        <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 lg:gap-6">

          {/* ── Mobile/tablet horizontal tab bar ── */}
          <div className="lg:hidden">
            <div className="bg-[#0D0F1A] border border-[#1E2030] rounded-2xl p-1.5 flex gap-1" style={{ scrollbarWidth: 'none' }}>
              {NAV_ITEMS.map(({ key, icon: Icon, labelKey }) => {
                const active = tabManage === key
                const label  = key === true ? General : ManageAgents
                return (
                  <button key={String(key)} onClick={() => setTabManage(key)}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all duration-200
                      ${active ? 'bg-[#6C5DD3] text-white shadow-[0_0_12px_rgba(108,93,211,0.3)]' : 'text-[#8B8FA8] hover:bg-[#161826] hover:text-white'}`}>
                    <Icon size={13} /> {label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* ── Desktop sidebar ── */}
          <aside className="hidden lg:block w-52 shrink-0">
            <nav className="bg-[#0D0F1A] border border-[#1E2030] rounded-2xl p-2 space-y-1">
              {NAV_ITEMS.map(({ key, icon: Icon }) => {
                const active = tabManage === key
                const label  = key === true ? General : ManageAgents
                return (
                  <button key={String(key)} onClick={() => setTabManage(key)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 text-left group
                      ${active ? 'bg-[#6C5DD3] text-white shadow-[0_0_16px_rgba(108,93,211,0.3)]' : 'text-[#8B8FA8] hover:bg-[#161826] hover:text-white'}`}>
                    <Icon size={16} className={active ? 'text-white' : 'text-[#8B8FA8] group-hover:text-[#6C5DD3]'} />
                    <span className="text-xs tracking-wide">{label}</span>
                    {active && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60" />}
                  </button>
                )
              })}
            </nav>
          </aside>

          {/* ── Main panel ── */}
          <main className="flex-1 min-w-0 bg-[#0D0F1A] border border-[#1E2030] rounded-2xl p-4 sm:p-6 lg:p-8">

            {/* ══ GENERAL / SETTINGS ══ */}
            {tabManage && (
              <div>
                <div className="flex items-start justify-between mb-5 pb-4 border-b border-[#1E2030]">
                  <div>
                    <h2 className="text-base sm:text-lg font-bold text-white">{Settings}</h2>
                    <p className="text-xs text-[#8B8FA8] mt-0.5">{Edit}</p>
                  </div>
                  <GlowBtn loading={loading?.general} onClick={handleUpdateAgent}>
                    <AiFillSave size={14} />
                    <span className="hidden sm:inline">{Save}</span>
                  </GlowBtn>
                </div>

                {/* Bank section header */}
                <div className="mb-5 p-4 bg-[#080A12] border border-[#1E2030] rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-[#161826] border border-[#2A2D3E] flex items-center justify-center shrink-0">
                      <PiBankDuotone size={18} className="text-[#6C5DD3]" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white">{Bank}</p>
                      <p className="text-xs text-[#8B8FA8]">{Agency}</p>
                    </div>
                  </div>
                </div>

                {/* Swift / BSB toggle */}
                <div className="flex gap-2 mb-5">
                  {[['swift', Swift], ['bsb', BSB]].map(([val, label]) => (
                    <button key={val}
                      onClick={() => { setButtonManage(val === 'swift'); setAccountData({ ...accountData, bank: val }) }}
                      className={`px-5 py-2 rounded-xl text-xs font-mono font-bold border transition-all uppercase
                        ${(val === 'swift' ? buttonManage : !buttonManage)
                          ? 'bg-[#6C5DD3] border-[#6C5DD3] text-white shadow-[0_0_12px_rgba(108,93,211,0.3)]'
                          : 'bg-[#080A12] border-[#2A2D3E] text-[#8B8FA8] hover:border-[#6C5DD3]/40 hover:text-white'}`}>
                      {label}
                    </button>
                  ))}
                </div>

                {/* Bank fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5">
                  <Field label={AccountHolder}>
                    <Input
                      placeholder="John Doe"
                      onChange={e => setAccountData({ ...accountData, acount_holder_name: e.target.value })}
                      value={accountData?.acount_holder_name}
                      size="large"
                      style={darkInputStyle}
                    />
                  </Field>
                  <Field label={BankName}>
                    <Input
                      onChange={e => setAccountData({ ...accountData, bank_name: e.target.value })}
                      placeholder="Deutsche Bank"
                      value={accountData?.bank_name}
                      size="large"
                      style={darkInputStyle}
                    />
                  </Field>
                </div>

                <Field label={BankAccount}>
                  <InputNumber
                    onChange={e => setAccountData({ ...accountData, account_number: e })}
                    className="w-full"
                    type="number"
                    placeholder="Bank Account Number"
                    value={accountData?.account_number}
                    size="large"
                    style={darkInputStyle}
                  />
                </Field>

                {/* KYC toggle */}
                <div className="mt-2 p-4 bg-[#080A12] border border-[#1E2030] rounded-xl">
                  <p className="text-xs font-bold text-[#6C5DD3] uppercase tracking-widest mb-1">{Require}</p>
                  <p className="text-xs text-[#8B8FA8] mb-4">{Wheather}</p>
                  <div className="flex items-center gap-4">
                    <Switch
                      checked={!!accountData?.require_kyc}
                      onChange={e => setAccountData({ ...accountData, require_kyc: e === true ? 1 : 0 })}
                    />
                    <div>
                      <p className="text-sm font-semibold text-white">{Enabled}</p>
                      <p className="text-xs text-[#8B8FA8] leading-5">{RequireKyc}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ══ MANAGE AGENTS ══ */}
            {!tabManage && (
              <div>
                <div className="mb-5 pb-4 border-b border-[#1E2030]">
                  <h2 className="text-base sm:text-lg font-bold text-white">{ManageUsers}</h2>
                  <p className="text-xs text-[#8B8FA8] mt-0.5">
                    {Array.isArray(agents) ? `${agents.length} agents` : 'Manage your agency agents'}
                  </p>
                </div>

                {/* Search + Add row */}
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                  <Select
                    size="large"
                    className="flex-1"
                    showSearch
                    placeholder="Search and select a user..."
                    optionFilterProp="label"
                    onChange={onChange}
                    onSearch={onSearch}
                    filterOption={false}
                    style={{ background: '#0D0F1A' }}
                    dropdownStyle={{ background: '#0D0F1A', border: '1px solid #1E2030', borderRadius: '12px' }}
                  >
                    {Array.isArray(users) && users.map(item => (
                      <Select.Option key={item?.id} value={item?.id}>
                        <span className="text-sm font-medium capitalize">{item?.name}</span>
                      </Select.Option>
                    ))}
                  </Select>
                  <GlowBtn
                    loading={loading?.agent}
                    disabled={selectUser === ''}
                    onClick={handleAddAgent}
                    className="sm:shrink-0"
                  >
                    {AddAgents}
                  </GlowBtn>
                </div>

                {/* Agents list */}
                {Array.isArray(agents) && agents.length > 0 ? (
                  <div className="space-y-2">
                    {/* Table header */}
                    <div className="hidden sm:grid grid-cols-12 px-4 py-2 text-xs font-semibold text-[#8B8FA8] uppercase tracking-widest">
                      <div className="col-span-7">Agent</div>
                      <div className="col-span-5">Email</div>
                    </div>

                    {agents.map((item) => (
                      <div key={item?.id} className="bg-[#080A12] border border-[#1E2030] rounded-xl hover:border-[#6C5DD3]/30 transition-colors">

                        {/* Mobile card */}
                        <div className="sm:hidden flex items-center gap-3 p-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold shrink-0 ${agentColor(item?.id)}`}>
                            {initials(item?.name)}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-white truncate">{item?.name}</p>
                            <p className="text-xs text-[#8B8FA8] truncate">{item?.email}</p>
                          </div>
                        </div>

                        {/* Desktop row */}
                        <div className="hidden sm:grid grid-cols-12 items-center px-4 py-3">
                          <div className="col-span-7 flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-bold shrink-0 ${agentColor(item?.id)}`}>
                              {initials(item?.name)}
                            </div>
                            <p className="text-sm font-bold text-white truncate">{item?.name}</p>
                          </div>
                          <div className="col-span-5">
                            <p className="text-xs text-[#8B8FA8] truncate">{item?.email}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center py-10">
                    <img className="w-full max-w-[260px] opacity-40 object-contain" src={image} alt="no agents" />
                    <h2 className="text-base font-bold text-white mt-6">{NoAgents}</h2>
                    <p className="text-xs text-[#8B8FA8] mt-1">{AgencyBank}</p>
                  </div>
                )}
              </div>
            )}

          </main>
        </div>
      </div>
    </div>
  )
}

export default AgencyManagement